import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePosts } from '@/hooks/usePosts';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Pagination from '@/components/base/Pagination';
import CatLoader from '@/components/base/CatLoader';
import AdSenseAd from '@/components/ads/AdSenseAd';
import Seo from '@/components/base/Seo';

const POSTS_PER_PAGE = 10;

export default function BlogPage() {
  const { getPublishedPosts, loading } = usePosts();
  const posts = getPublishedPosts();

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });

  const totalPages = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE));

  // keep page in bounds if posts change
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const pagePosts = posts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams(page === 1 ? {} : { page: String(page) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <CatLoader fullPage message="Buscando os artigos..." />
        <Footer popularPosts={[]} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Seo
        title="Blog sobre gatos"
        description="Artigos sobre saúde felina, alimentação, comportamento, cuidados, curiosidades e bem-estar dos gatos."
        path="/blog"
      />
      <Navbar />

      <div>
        {/* Header */}
        <div className="bg-background-100 py-8 sm:py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <span className="text-primary-500 text-xs font-bold tracking-widest uppercase block mb-2">
              Todos os artigos
            </span>
            <h1 className="font-heading font-bold text-foreground-950 text-2xl sm:text-3xl md:text-4xl">
              Blog Miaudecasa
            </h1>
            {posts.length > 0 && (
              <p className="text-foreground-500 text-sm mt-2">
                {posts.length} artigo{posts.length !== 1 ? 's' : ''} publicado{posts.length !== 1 ? 's' : ''}
                {totalPages > 1 && (
                  <span className="ml-2 text-foreground-400">
                    — página {currentPage} de {totalPages}
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Articles Grid */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <AdSenseAd placement="listing" className="mb-8 md:mb-10" />
          {posts.length === 0 ? (
            <div className="text-center py-16 md:py-20 text-foreground-500">
              <i className="ri-file-list-3-line text-4xl md:text-5xl mb-3 md:mb-4 block"></i>
              <p>Nenhum artigo publicado ainda.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {pagePosts.map((post, i) => (
                  <div
                    key={post.id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${i * 50}ms`, animationFillMode: 'both' }}
                  >
                    <ArticleCard post={post} />
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />

              {/* Post range info */}
              {totalPages > 1 && (
                <p className="text-center text-foreground-400 text-xs mt-4">
                  Exibindo {(currentPage - 1) * POSTS_PER_PAGE + 1}–{Math.min(currentPage * POSTS_PER_PAGE, posts.length)} de {posts.length} artigos
                </p>
              )}
            </>
          )}
        </div>

      </div>

      <Footer popularPosts={posts} />
    </div>
  );
}
